import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface CookieCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
}

const CookiePage: React.FC = () => {
  const cookieCategories: CookieCategory[] = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      description: 'These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms.',
      required: true
    },
    {
      id: 'performance',
      name: 'Performance Cookies',
      description: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site.',
      required: false
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third party providers whose services we have added to our pages.',
      required: false
    },
    {
      id: 'targeting',
      name: 'Targeting Cookies',
      description: 'These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant advertisements on other sites.',
      required: false
    }
  ];
  
  const [cookiePreferences, setCookiePreferences] = useState<{[key: string]: boolean}>({
    essential: true, // Always true
    performance: true,
    functional: true,
    targeting: false
  });
  
  const handleToggle = (id: string) => {
    if (id === 'essential') return; // Cannot toggle essential cookies
    
    setCookiePreferences(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  const handleAcceptAll = () => {
    setCookiePreferences({
      essential: true,
      performance: true,
      functional: true,
      targeting: true
    });
  };
  
  const handleRejectNonEssential = () => {
    setCookiePreferences({
      essential: true,
      performance: false,
      functional: false,
      targeting: false
    });
  };
  
  const handleSavePreferences = () => {
    // In a real application, this would save to cookies/localStorage
    alert('Your cookie preferences have been saved.');
  };
  
  return (
    <main>
      <div className="container">
        <div style={{ marginTop: '120px', marginBottom: '50px' }}>
          <h1 className="section-title">Cookie Preferences</h1>
          
          <div style={{ marginTop: '30px', lineHeight: '1.6' }}>
            <p style={{ marginBottom: '20px' }}>
              Quick Flick uses cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content.
              This page allows you to customize your cookie preferences for our website.
            </p>
            
            <div style={{ 
              marginBottom: '30px',
              background: 'var(--secondary-color)',
              borderRadius: '8px',
              padding: '25px',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <button 
                  onClick={handleAcceptAll}
                  className="btn btn-primary"
                >
                  Accept All Cookies
                </button>
                <button 
                  onClick={handleRejectNonEssential}
                  className="btn btn-secondary"
                >
                  Essential Cookies Only
                </button>
              </div>
            </div>
            
            <h2 style={{ fontSize: '1.5rem', margin: '30px 0 15px' }}>Manage Cookie Preferences</h2>
            <p style={{ marginBottom: '20px' }}>
              You can choose which types of cookies you allow us to use. Click on the different category headings to learn more and change our default settings.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '30px' }}>
              {cookieCategories.map(category => (
                <div 
                  key={category.id}
                  style={{ 
                    background: 'var(--secondary-color)',
                    borderRadius: '8px',
                    padding: '20px'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '10px'
                  }}>
                    <h3>{category.name}</h3>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {category.required && (
                        <span style={{ marginRight: '15px', fontSize: '0.8rem', color: 'var(--light-gray)' }}>
                          Required
                        </span>
                      )}
                      <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '60px', height: '34px' }}>
                        <input 
                          type="checkbox" 
                          checked={cookiePreferences[category.id]} 
                          onChange={() => handleToggle(category.id)}
                          disabled={category.required}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span 
                          style={{ 
                            position: 'absolute',
                            cursor: category.required ? 'default' : 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: cookiePreferences[category.id] ? 'var(--primary-color)' : 'var(--gray-color)',
                            transition: '.4s',
                            borderRadius: '34px',
                          }}
                        >
                          <span 
                            style={{ 
                              position: 'absolute',
                              content: '""',
                              height: '26px',
                              width: '26px',
                              left: cookiePreferences[category.id] ? '26px' : '4px',
                              bottom: '4px',
                              backgroundColor: 'white',
                              transition: '.4s',
                              borderRadius: '50%'
                            }}
                          />
                        </span>
                      </label>
                    </div>
                  </div>
                  <p style={{ color: 'var(--light-gray)' }}>{category.description}</p>
                </div>
              ))}
            </div>
            
            <h2 style={{ fontSize: '1.5rem', margin: '30px 0 15px' }}>About Cookies</h2>
            <p style={{ marginBottom: '15px' }}>
              Cookies are small text files that are stored on your browser or device by websites, apps, online media, and advertisements. 
              They are used for various purposes, including authenticating users, remembering preferences, tracking user behavior, and more.
            </p>
            
            <h2 style={{ fontSize: '1.5rem', margin: '30px 0 15px' }}>How We Use Cookies</h2>
            <p style={{ marginBottom: '15px' }}>
              At Quick Flick, we use cookies to:
            </p>
            <ul style={{ marginLeft: '20px', marginBottom: '20px', listStyleType: 'disc' }}>
              <li>Remember your login details and prevent you from having to log in each time you visit our site</li>
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our service so we can improve it</li>
              <li>Personalize content and recommendations based on your viewing history</li>
              <li>Measure the effectiveness of our marketing campaigns</li>
              <li>Detect and prevent fraud and abuse</li>
            </ul>
            
            <h2 style={{ fontSize: '1.5rem', margin: '30px 0 15px' }}>Managing Cookies in Your Browser</h2>
            <p style={{ marginBottom: '15px' }}>
              In addition to the controls we provide, you can choose to restrict, block, or delete cookies from any website through your browser settings.
              For more information about how to do this, visit:
            </p>
            <ul style={{ marginLeft: '20px', marginBottom: '20px', listStyleType: 'disc' }}>
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>Microsoft Edge</a></li>
            </ul>
            <p style={{ marginBottom: '20px' }}>
              Please note that restricting cookies may impact your experience of our website, as some features may not function properly.
            </p>
            
            <div style={{ 
              marginTop: '40px',
              textAlign: 'center'
            }}>
              <button 
                onClick={handleSavePreferences}
                className="btn btn-primary"
                style={{ padding: '12px 30px', fontSize: '1.1rem' }}
              >
                Save Preferences
              </button>
              <p style={{ marginTop: '15px', fontSize: '0.9rem', color: 'var(--light-gray)' }}>
                For more information, please see our <Link to="/privacy" style={{ color: 'var(--primary-color)' }}>Privacy Policy</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default CookiePage; 